import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_DIVIDER_SX, LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    actionType: 'ALL',
    isActive: 'ALL',
}

const SEARCH_SELECT_PARAMS = {
    actionType: {
        param: 'action',
        defaultValue: DEFAULT_SEARCH_SELECT.actionType,
    },
    isActive: {
        param: 'active',
        defaultValue: DEFAULT_SEARCH_SELECT.isActive,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

export default function AppAccessRuleList() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current

    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10

    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)

    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const deleteInFlightRef = useRef(false)
    const silentListRefreshRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appAccessRule/getList', {
                page: curPage,
                offset,
                searchText: searchQuery,
                searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setData(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '앱 접근 정책 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '앱 접근 정책 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        try {
            const { data: response } = await API.post('/api/v1/admin/appAccessRule/delete', {
                appAccessRuleId: id
            })

            if (response.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(response.message)

                if (curPage > 1 && data.length === 1) {
                    setCurPage(curPage - 1)
                } else {
                    await getData(false)
                }
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('앱 접근 정책을 삭제하지 못했습니다.')
        } finally {
            deleteInFlightRef.current = false
            setActionLoading(false)
        }
    }

    const updateSearchSelect = (e) => {
        if (actionLoading) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({ ...DEFAULT_SEARCH_SELECT })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const hasActiveFilters = Boolean(
        searchQuery ||
        searchSelect.actionType !== DEFAULT_SEARCH_SELECT.actionType ||
        searchSelect.isActive !== DEFAULT_SEARCH_SELECT.isActive
    )

    // 목록 상태에 맞는 빈 화면을 한 곳에서 결정한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="앱 접근 정책 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button variant="outlined" size="small" onClick={() => getData(false)} disabled={actionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 앱 접근 정책 룰이 없습니다."
                    description="제목/내용 검색어와 Action, 활성여부 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 앱 접근 정책 룰이 없습니다."
                description="앱 접근 정책 룰을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>앱 접근 정책 룰</Title>

                    {/* 검색 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="제목/내용 검색"
                                type="search"
                                value={searchText}
                                disabled={actionLoading}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" disabled={actionLoading}>
                                <SearchIcon />
                            </IconButton>

                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />

                            <TextField
                                select
                                label="Action"
                                name="actionType"
                                size="small"
                                value={searchSelect.actionType}
                                onChange={updateSearchSelect}
                                disabled={actionLoading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value="ALL">전체</MenuItem>
                                <MenuItem value="FORCE_EXIT">강제종료</MenuItem>
                                <MenuItem value="NOTICE">공지</MenuItem>
                            </TextField>

                            <TextField
                                select
                                label="활성여부"
                                name="isActive"
                                size="small"
                                value={searchSelect.isActive}
                                onChange={updateSearchSelect}
                                disabled={actionLoading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value="ALL">전체</MenuItem>
                                <MenuItem value="true">활성</MenuItem>
                                <MenuItem value="false">비활성</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>

                    {/* 테이블 */}
                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appAccessRule',
                                    search: location.search,
                                    curPage,
                                    searchText: searchQuery,
                                    searchSelect
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: 900 }}>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>제목</TableCell>
                                    <TableCell>내용</TableCell>
                                    <TableCell>우선순위</TableCell>
                                    <TableCell>조건/타겟</TableCell>
                                    <TableCell>활성</TableCell>
                                    <TableCell>등록일</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? (
                                    data.map((row) => (
                                        <TableRow key={row.appAccessRuleId}>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell>{row.actionType === 'FORCE_EXIT' ? '강제종료' : '공지'}</TableCell>
                                            <TableCell className="listTableLimitText">{row.title}</TableCell>
                                            <TableCell className="listTableLimitText">{row.content}</TableCell>
                                            <TableCell>{row.priority}</TableCell>
                                            <TableCell>{row.conditionCount + '/' + row.targetCount}</TableCell>
                                            <TableCell>{row.isActive ? 'Y' : 'N'}</TableCell>
                                            <TableCell>{row.regTime}</TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup disabled={actionLoading}>
                                                    <EditIcon path="appAccessRule" id={row.appAccessRuleId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} />
                                                    <DeleteAlertDialog handleDelete={handleDelete} id={row.appAccessRuleId} />
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>

                    {totalCount > 0 && (
                        <Pagination
                            curPage={curPage}
                            offset={offset}
                            totalCount={totalCount}
                            setCurPage={setCurPage}
                        />
                    )}

                    {openSnackbar && (
                        <Snackbars
                            open={openSnackbar}
                            severity={severitySnackbar}
                            message={messageSnackbar}
                            handleClose={() => setOpenSnackbar(false)}
                        />
                    )}
                </Paper>
            )}
        </Grid>
    )
}
